/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AnnotationPropertySpec, annotationPropertySpecsToJson, AnnotationType, LocalAnnotationSource, parseAnnotationPropertySpecs } from 'neuroglancer/annotation';
import { AnnotationDisplayState, AnnotationLayerState } from 'neuroglancer/annotation/annotation_layer_state';
import { MultiscaleAnnotationSource } from 'neuroglancer/annotation/frontend_source';
import { CoordinateTransformSpecification, makeCoordinateSpace } from 'neuroglancer/coordinate_transform';
import { DataSourceSpecification, localAnnotationsUrl, LocalDataSource } from 'neuroglancer/datasource';
import { LayerManager, LayerReference, ManagedUserLayer, registerLayerType, registerLayerTypeDetector, UserLayer } from 'neuroglancer/layer';
import { LoadedDataSubsource } from 'neuroglancer/layer_data_source';
import { Overlay } from 'neuroglancer/overlay';
import { RenderLayerRole } from 'neuroglancer/renderlayer';
import { getWatchableRenderLayerTransform, RenderLayerTransform } from 'neuroglancer/render_coordinate_transform';
// import { SegmentationDisplayState } from 'neuroglancer/segmentation_display_state/frontend';
import { SegmentationDisplayState, bindSegmentListWidth, augmentSegmentId } from 'neuroglancer/segmentation_display_state/frontend';
import { SegmentationUserLayer,SegmentationUserLayerDisplayState } from 'neuroglancer/segmentation_user_layer';
import { TrackableBoolean, TrackableBooleanCheckbox } from 'neuroglancer/trackable_boolean';
import { makeCachedLazyDerivedWatchableValue, TrackableValue, WatchableValue, registerNestedSync} from 'neuroglancer/trackable_value';
import { AnnotationLayerView, MergedAnnotationStates, UserLayerWithAnnotationsMixin } from 'neuroglancer/ui/annotations';
import { animationFrameDebounce } from 'neuroglancer/util/animation_frame_debounce';
import { Borrowed, Owned, RefCounted } from 'neuroglancer/util/disposable';
import { removeFromParent, updateChildren } from 'neuroglancer/util/dom';
import { parseArray, parseFixedLengthArray, stableStringify, verify3dVec, verifyFinitePositiveFloat, verifyObject, verifyObjectAsMap, verifyOptionalObjectProperty, verifyString, verifyStringArray } from 'neuroglancer/util/json';
import { NullarySignal } from 'neuroglancer/util/signal';
import { DependentViewWidget } from 'neuroglancer/widget/dependent_view_widget';
import { cellShapeModeWidget } from 'neuroglancer/widget/cell_shape_mode';
import { makeHelpButton } from 'neuroglancer/widget/help_button';
import { LayerReferenceWidget } from 'neuroglancer/widget/layer_reference';
import { makeMaximizeButton } from 'neuroglancer/widget/maximize_button';
import { RenderScaleWidget } from 'neuroglancer/widget/render_scale_widget';
import { ShaderCodeWidget } from 'neuroglancer/widget/shader_code_widget';
import { registerLayerShaderControlsTool, ShaderControls } from 'neuroglancer/widget/shader_controls';
import { Tab } from 'neuroglancer/widget/tab_view';
import { viewer as _layoutviewer, hasCluster, viewer, local_host_SN_flag, allToolsButton, h5ad_file_binsize } from 'src/main';
import { MultiscaleMeshLayer } from '../mesh/frontend';
// import { StatusMessage } from '../status';
import { DataSourceView, SourceUrlAutocomplete } from '../ui/layer_data_sources_tab';
import { SegmentDisplayTab } from '../ui/segment_list';
import { Uint64 } from '../util/uint64';
import './user_layer.css';
// ***
import { getPreprocessedSegmentPropertyMap, SegmentPropertyMap } from 'neuroglancer/segmentation_display_state/property_map';
import { SegmentationGraphSource } from 'neuroglancer/segmentation_graph/source';
import { packColor, parseRGBColorSpecification, serializeColor, unpackRGB } from '../util/color';
import { makeElement } from '../ui/layer_gene_table_tab';
import { ToolControl } from '../widget/tool_control';
import i18n from '../i18n/i18n';
import { message } from '../widget/message';
import { SelectView } from '../widget/select_view';
import { tissueGefOutline } from '../ui/tissue';

const POINTS_JSON_KEY = 'points';
const ANNOTATIONS_JSON_KEY = 'annotations';
const GENE_TYPE = 'genetype';
const ANNOTATION_PROPERTIES_JSON_KEY = 'annotationProperties';
const ANNOTATION_RELATIONSHIPS_JSON_KEY = 'annotationRelationships';
const CROSS_SECTION_RENDER_SCALE_JSON_KEY = 'crossSectionAnnotationSpacing';
const PROJECTION_RENDER_SCALE_JSON_KEY = 'projectionAnnotationSpacing';
const SHADER_JSON_KEY = 'shader';
const SHADER_CONTROLS_JSON_KEY = 'shaderControls';
const SEGMENT_STATED_COLORS_JSON_KEY = 'segmentColors';
const SEGMENTS_JSON_KEY = 'segments';
export let colorArr = [
  {colorName: 'Default', colormap: ['#0c3383', '#005ea3', '#0a88ba', '#00c199', '#f2d338', '#F6b132', '#f28f38', '#e76124', '#d91e1e', '#d91e1e'], reversed: false, clicked: true},
  {colorName: 'newColor_1', colormap: ['#3F3B72', '#3F3B72', '#617CB8', '#617CB8', '#CED9EB', '#CED9EB', '#DF9B92', '#DF9B92', '#C06B5E', '#C06B5E'], reversed: false, clicked: false},
  {colorName: 'newColor_2', colormap: ['#11325D', '#11325D', '#365083', '#736B9D', '#736B9D', '#B783AF', '#F5A673', '#FCDB72', '#FCDB72', '#FCDB72'], reversed: false, clicked: false},
  {colorName: 'newColor_3', colormap: ['#081D59', '#21318C', '#2254A3', '#1E80B8', '#30A5C2', '#5BBFC0', '#95D5B8', '#CDEBB3', '#EEF8B4', '#FEFED7'], reversed: false, clicked: false},
  {colorName: 'newColor_4', colormap: ['#023047', '#0E5B76', '#1A86A3', '#46ACCA', '#9BCFE8', '#F3F9FC', '#FFCA5F', '#FEA809', '#FD9802', '#FB8402'], reversed: false, clicked: false},
  {colorName: 'newColor_5', colormap: ['#A80326','#DA382A','#F67948', '#FDB96B', '#FEE99D', '#F4FBD3', '#CAE8F2', '#92C5DE', '#5C90C2', '#3951A2'], reversed: false, clicked: false},
  {colorName: 'newColor_6', colormap: ['#104680','#317CB7','#6DADD1', '#E9F1F4', '#FBE3D5', '#F6B293', '#DC6D57', '#B72230', '#6D011F', '#6D011F'], reversed: false, clicked: false},
  {colorName: 'newColor_7', colormap: ['#40037E', '#40037E','#5B3595', '#796FB3', '#9C98C7', '#9C98C7', '#C5C5E0', '#E5E3F0', '#FAF9FC', '#FAF9FC'], reversed: false, clicked: false},
  {colorName: 'Viridis', colormap: ['#440154','#482878','#3e4989','#31688e','#26828e','#1f9e89','#35b779','#6ece58','#b5de2b','#fde725'], reversed: false, clicked: false},
  {colorName: 'Cividis', colormap: ['#00224e','#123570','#3b496c','#575d6d','#707173','#8a8678','#a59c74','#c3b369','#e1cc55','#fee838'], reversed: false, clicked: false},
  {colorName: 'Inferno', colormap: ['#000004','#1b0c41','#4a0c6b','#781c6d','#a52c60','#cf4446','#ed6925','#fb9b06','#f7d13d','#fcffa4'], reversed: false, clicked: false},
  {colorName: 'Plasma', colormap: ['#0d0887','#46039f','#7201a8','#9c179e','#bd3786','#d8576b','#ed7953','#fb9f3a','#fdca26','#f0f921'], reversed: false, clicked: false},
  {colorName: 'Purples', colormap: ['#fcfbfd','#efedf5','#dadaeb','#bcbddc','#9e9ac8','#807dba','#6a51a3','#54278f','#3f007d','#3f007d'], reversed: false, clicked: false},
  {colorName: 'Blues', colormap: ['#f7fbff','#deebf7','#c6dbef','#9ecae1','#6baed6','#4292c6','#2171b5','#08519c','#08306b','#08306b'], reversed: false, clicked: false},
  {colorName: 'Greens', colormap: ['#f7fcf5','#e5f5e0','#c7e9c0','#a1d99b','#74c476','#41ab5d','#238b45','#006d2c','#00441b','#00441b'], reversed: false, clicked: false},
  {colorName: 'Oranges', colormap: ['#fff5eb','#fee6ce','#fdd0a2','#fdae6b','#fd8d3c','#f16913','#d94801','#a63603','#7f2704','#7f2704'], reversed: false, clicked: false}
]

function addPointAnnotations(annotations: LocalAnnotationSource, obj: any) {
  if (obj === undefined) {
    return;
  }
  parseArray(obj, (x, i) => {
    annotations.add({
      type: AnnotationType.POINT,
      id: '' + i,
      point: verify3dVec(x),
      properties: [],
    });
  });
}

function isValidLinkedSegmentationLayer(layer: ManagedUserLayer) {
  const userLayer = layer.layer;
  if (userLayer === null) {
    return true;
  }
  if (userLayer instanceof SegmentationUserLayer) {
    return true;
  }
  return false;
}

function getSegmentationDisplayState(layer: ManagedUserLayer|undefined): SegmentationDisplayState|
    null {
  if (layer === undefined) {
    return null;
  }
  const userLayer = layer.layer;
  if (userLayer === null) {
    return null;
  }
  if (!(userLayer instanceof SegmentationUserLayer)) {
    return null;
  }
  return userLayer.displayState;
}

interface LinkedSegmentationLayer {
  layerRef: Owned<LayerReference>;
  showMatches: TrackableBoolean;
  seenGeneration: number;
}

const LINKED_SEGMENTATION_LAYER_JSON_KEY = 'linkedSegmentationLayer';
const FILTER_BY_SEGMENTATION_JSON_KEY = 'filterBySegmentation';
const IGNORE_NULL_SEGMENT_FILTER_JSON_KEY = 'ignoreNullSegmentFilter';

class LinkedSegmentationLayers extends RefCounted {
  changed = new NullarySignal();
  private curGeneration = -1;
  private wasLoading: boolean|undefined = undefined;
  constructor(
      public layerManager: Borrowed<LayerManager>,
      public annotationStates: Borrowed<MergedAnnotationStates>,
      public annotationDisplayState: Borrowed<AnnotationDisplayState>) {
    super();
    this.registerDisposer(annotationStates.changed.add(() => this.update()));
    this.registerDisposer(annotationStates.isLoadingChanged.add(() => this.update()));
    this.update();
  }

  private update() {
    const generation = this.annotationStates.changed.count;
    const isLoading = this.annotationStates.isLoading;
    if (this.curGeneration === generation && isLoading === this.wasLoading) return;
    this.wasLoading = isLoading;
    this.curGeneration = generation;
    const {map} = this;
    let changed = false;
    for (const relationship of this.annotationStates.relationships) {
      let state = map.get(relationship);
      if (state === undefined) {
        state = this.addRelationship(relationship);
        changed = true;
      }
      state.seenGeneration = generation;
    }
    if (!isLoading) {
      for (const [relationship, state] of map) {
        if (state.seenGeneration !== generation) {
          map.delete(relationship);
          changed = true;
        }
      }
    }
    if (changed) {
      this.changed.dispatch();
    }
  }

  private addRelationship(relationship: string): LinkedSegmentationLayer {
    const relationshipState = this.annotationDisplayState.relationshipStates.get(relationship);
    const layerRef = new LayerReference(this.layerManager.addRef(), isValidLinkedSegmentationLayer);
    layerRef.registerDisposer(layerRef.changed.add(() => {
      relationshipState.segmentationState.value = layerRef.layerName === undefined ?
          undefined :
          getSegmentationDisplayState(layerRef.layer);
    }));
    const {showMatches} = relationshipState;
    const state = {
      layerRef,
      showMatches,
      seenGeneration: -1,
    };
    layerRef.changed.add(this.changed.dispatch);
    showMatches.changed.add(this.changed.dispatch);
    this.map.set(relationship, state);
    return state;
  }

  get(relationship: string): LinkedSegmentationLayer {
    this.update();
    return this.map.get(relationship)!;
  }

  private unbind(state: LinkedSegmentationLayer) {
    state.layerRef.changed.remove(this.changed.dispatch);
    state.showMatches.changed.remove(this.changed.dispatch);
  }

  reset() {
    for (const state of this.map.values()) {
      state.showMatches.reset();
    }
  }

  toJSON() {
    const {map} = this;
    if (map.size === 0) return {};
    let linkedJson: {[relationship: string]: string}|undefined = undefined;
    const filterBySegmentation = [];
    for (const [name, state] of map) {
      if (state.showMatches.value) {
        filterBySegmentation.push(name);
      }
      const {layerName} = state.layerRef;
      if (layerName !== undefined) {
        (linkedJson = linkedJson || {})[name] = layerName;
      }
    }
    filterBySegmentation.sort();
    return {
      [LINKED_SEGMENTATION_LAYER_JSON_KEY]: linkedJson,
      [FILTER_BY_SEGMENTATION_JSON_KEY]:
          filterBySegmentation.length === 0 ? undefined : filterBySegmentation,
    };
  }
  restoreState(json: any) {
    const {isLoading} = this.annotationStates;
    verifyOptionalObjectProperty(json, LINKED_SEGMENTATION_LAYER_JSON_KEY, linkedJson => {
      if (typeof linkedJson === 'string') {
        linkedJson = {'segments': linkedJson};
      }
      verifyObject(linkedJson);
      for (const key of Object.keys(linkedJson)) {
        const value = verifyString(linkedJson[key]);
        let state = this.map.get(key);
        if (state === undefined) {
          if (!isLoading) continue;
          state = this.addRelationship(key);
        }
        state.layerRef.layerName = value;
      }
      for (const [relationship, state] of this.map) {
        if (!Object.prototype.hasOwnProperty.call(linkedJson, relationship)) {
          state.layerRef.layerName = undefined;
        }
      }
    });
    verifyOptionalObjectProperty(json, FILTER_BY_SEGMENTATION_JSON_KEY, filterJson => {
      if (typeof filterJson === 'boolean') {
        filterJson = (filterJson === true) ? ['segments'] : [];
      }
      for (const key of verifyStringArray(filterJson)) {
        let state = this.map.get(key);
        if (state === undefined) {
          if (!isLoading) continue;
          state = this.addRelationship(key);
        }
        state.showMatches.value = true;
      }
    });
  }

  disposed() {
    const {map} = this;
    for (const state of map.values()) {
      this.unbind(state);
    }
    map.clear();
    super.disposed();
  }
  private map = new Map<string, LinkedSegmentationLayer>();
}

class LinkedSegmentationLayerWidget extends RefCounted {
  element = document.createElement('label');
  seenGeneration = -1;
  constructor(public relationship: string, public state: LinkedSegmentationLayer) {
    super();
    const {element} = this;
    const checkboxWidget = this.registerDisposer(new TrackableBooleanCheckbox(state.showMatches));
    const layerWidget = new LayerReferenceWidget(state.layerRef);
    element.appendChild(checkboxWidget.element);
    element.appendChild(document.createTextNode(relationship));
    element.appendChild(layerWidget.element);
  }
}

class LinkedSegmentationLayersWidget extends RefCounted {
  widgets = new Map<string, LinkedSegmentationLayerWidget>();
  element = document.createElement('div');
  constructor(public linkedSegmentationLayers: LinkedSegmentationLayers) {
    super();
    this.element.style.display = 'contents';
    const debouncedUpdateView =
        this.registerCancellable(animationFrameDebounce(() => this.updateView()));
    this.registerDisposer(
        this.linkedSegmentationLayers.annotationStates.changed.add(debouncedUpdateView));
    this.updateView();
  }

  private updateView() {
    const {linkedSegmentationLayers} = this;
    const {annotationStates} = linkedSegmentationLayers;
    const generation = annotationStates.changed.count;
    const {widgets} = this;
    function* getChildren(this: LinkedSegmentationLayersWidget) {
      for (const relationship of annotationStates.relationships) {
        let widget = widgets.get(relationship);
        if (widget === undefined) {
          widget = new LinkedSegmentationLayerWidget(
              relationship, linkedSegmentationLayers.get(relationship));
        }
        widget.seenGeneration = generation;
        yield widget.element;
      }
    }
    for (const [relationship, widget] of widgets) {
      if (widget.seenGeneration !== generation) {
        widget.dispose();
        widgets.delete(relationship);
      }
    }
    updateChildren(this.element, getChildren.call(this));
  }

  disposed() {
    super.disposed();
    for (const widget of this.widgets.values()) {
      widget.dispose();
    }
  }
}

const Base = UserLayerWithAnnotationsMixin(UserLayer);
export class AnnotationUserLayer extends Base {
  localAnnotations: LocalAnnotationSource|undefined;
  private localAnnotationProperties: AnnotationPropertySpec[]|undefined;
  private localAnnotationRelationships: string[];
  private localAnnotationsJson: any = undefined;
  private pointAnnotationsJson: any = undefined;
  isCluster: boolean; // 判定是否为聚类数据，用来区分shader逻辑
  segmentColors: any;
  segments: Array<any>;
  linkedSegmentationLayers = this.registerDisposer(new LinkedSegmentationLayers(
      this.manager.rootLayers, this.annotationStates, this.annotationDisplayState));

  disposed() {
    const {localAnnotations} = this;
    if (localAnnotations !== undefined) {
      localAnnotations.dispose();
    }

    this.handleGlobalCluser('deleteObserver')

    super.disposed();
  }
  // ***cellboer新增
  bindSegmentListWidth(element: HTMLElement) {
    return bindSegmentListWidth(this.displayState, element);
  }
  segmentQueryFocusTime = new WatchableValue<number>(Number.NEGATIVE_INFINITY);
  selectSegment = (id: Uint64, pin: boolean | 'toggle') => {
    this.manager.root.selectionState.captureSingleLayerState(this, state => {
      state.value = id.clone();
      return true;
    }, pin);
  };

  filterBySegmentLabel = (id: Uint64) => {
    const augmented = augmentSegmentId(this.displayState, id);
    const { label } = augmented;
    if (!label) return;
    this.filterSegments(label);
  };

  filterSegments = (query: string) => {
    this.displayState.segmentationGroupState.value.segmentQuery.value = query;
    this.segmentQueryFocusTime.value = Date.now();
    this.tabs.value = 'segments';
    this.manager.root.selectedLayer.layer = this.managedLayer;
  };
  displayState = new SegmentationUserLayerDisplayState (this);

  anchorSegment = new TrackableValue<Uint64 | undefined>(
    undefined, x => x === undefined ? undefined : Uint64.parseString(x));
  // ***
  constructor(managedLayer: Borrowed<ManagedUserLayer>) {
    super(managedLayer);
    // ***
    this.registerDisposer(registerNestedSync((context, group) => {
      context.registerDisposer(group.specificationChanged.add(this.specificationChanged.dispatch));
      this.specificationChanged.dispatch();
    }, this.displayState.segmentationGroupState));
    this.registerDisposer(registerNestedSync((context, group) => {
      context.registerDisposer(group.specificationChanged.add(this.specificationChanged.dispatch));
      this.specificationChanged.dispatch();
    }, this.displayState.segmentationColorGroupState));
    this.anchorSegment.changed.add(this.specificationChanged.dispatch);
    this.displayState.linkedSegmentationGroup.changed.add(
      () => this.updateDataSubsourceActivations());
    // ***
    this.linkedSegmentationLayers.changed.add(this.specificationChanged.dispatch);
    this.annotationDisplayState.ignoreNullSegmentFilter.changed.add(
        this.specificationChanged.dispatch);
    this.annotationCrossSectionRenderScaleTarget.changed.add(this.specificationChanged.dispatch);
    this.tabs.add(
        'rendering',
        {label: 'Rendering', order: -100, getter: () => new RenderingOptionsTab(this)});
    this.tabs.add(
      'segments', {label: 'Segment', order: -50, getter: () => new SegmentDisplayTab (this)});
    this.tabs.default = 'annotations';

    // 增加observer，做全局联动
    // debugger
    if (hasCluster(managedLayer.name)) {
      this.handleGlobalCluser('addObserver')
    }

  }

  handleGlobalCluser(funcName: string) {
    const {globalVisibleSegments, globalStatedColor} = _layoutviewer
    const {visibleSegments} = this.displayState.segmentationGroupState.value;
    const {value: segmentStatedColors} = this.displayState.segmentStatedColors;
    globalVisibleSegments[funcName] && globalVisibleSegments[funcName](visibleSegments)
    globalStatedColor[funcName] && globalStatedColor[funcName](segmentStatedColors)
  }

  restoreState(specification: any) {
    super.restoreState(specification);
    this.linkedSegmentationLayers.restoreState(specification);
    this.isCluster = specification[GENE_TYPE] === 'segmentation'
    this.localAnnotationsJson = specification[ANNOTATIONS_JSON_KEY];
    this.localAnnotationProperties = verifyOptionalObjectProperty(
        specification, ANNOTATION_PROPERTIES_JSON_KEY, parseAnnotationPropertySpecs);
    this.localAnnotationRelationships = verifyOptionalObjectProperty(
        specification, ANNOTATION_RELATIONSHIPS_JSON_KEY, verifyStringArray, ['segments']);
    this.pointAnnotationsJson = specification[POINTS_JSON_KEY];
    this.annotationCrossSectionRenderScaleTarget.restoreState(
        specification[CROSS_SECTION_RENDER_SCALE_JSON_KEY]);
    this.annotationProjectionRenderScaleTarget.restoreState(
        specification[PROJECTION_RENDER_SCALE_JSON_KEY]);
    this.annotationDisplayState.ignoreNullSegmentFilter.restoreState(
        specification[IGNORE_NULL_SEGMENT_FILTER_JSON_KEY]);
    this.annotationDisplayState.shader.restoreState(specification[SHADER_JSON_KEY]);
    this.annotationDisplayState.shaderControls.restoreState(
        specification[SHADER_CONTROLS_JSON_KEY]);
    // verifyOptionalObjectProperty(specification, SEGMENT_STATED_COLORS_JSON_KEY, y => {
    //   this.segmentColors = y
    // });
    verifyOptionalObjectProperty(specification, SEGMENT_STATED_COLORS_JSON_KEY, y => {
      const {value: segmentStatedColors} = this.displayState.segmentStatedColors;
      let result = verifyObjectAsMap(y, x => parseRGBColorSpecification(String(x)));
      for (let [idStr, colorVec] of result) {
        const id = Uint64.parseString(String(idStr));
        const color = new Uint64(packColor(colorVec));
        segmentStatedColors.value.set(id, color);
      }
    });

    verifyOptionalObjectProperty(specification, SEGMENTS_JSON_KEY, segmentsValue => {
      const {segmentEquivalences, visibleSegments} = this.displayState.segmentationGroupState.value;
      parseArray(segmentsValue, value => {
        let id = Uint64.parseString(String(value), 10);
        visibleSegments.add(segmentEquivalences.get(id));
      });
    });
    this.segments = specification[SEGMENTS_JSON_KEY];
  }

  getLegacyDataSourceSpecifications(
      sourceSpec: any, layerSpec: any, legacyTransform: CoordinateTransformSpecification|undefined,
      explicitSpecs: DataSourceSpecification[]): DataSourceSpecification[] {
    if (Object.prototype.hasOwnProperty.call(layerSpec, 'source')) {
      return super.getLegacyDataSourceSpecifications(
          sourceSpec, layerSpec, legacyTransform, explicitSpecs);
    }
    const scales = verifyOptionalObjectProperty(
        layerSpec, 'voxelSize',
        voxelSizeObj => parseFixedLengthArray(
            new Float64Array(3), voxelSizeObj, x => verifyFinitePositiveFloat(x) / 1e9));
    const units = ['m', 'm', 'm'];
    if (scales !== undefined) {
      const inputSpace = makeCoordinateSpace({rank: 3, units, scales, names: ['x', 'y', 'z']});
      if (legacyTransform === undefined) {
        legacyTransform = {
          outputSpace: inputSpace,
          sourceRank: 3,
          transform: undefined,
          inputSpace,
        };
      } else {
        legacyTransform = {
          ...legacyTransform,
          inputSpace,
        };
      }
    }
    return [{
      url: localAnnotationsUrl,
      transform: legacyTransform,
      enableDefaultSubsources: true,
      subsources: new Map(),
    }];
  }

  activateDataSubsources(subsources: Iterable<LoadedDataSubsource>) {
    let hasLocalAnnotations = false;
    let properties: AnnotationPropertySpec[]|undefined;
    // ***
    let updatedSegmentPropertyMaps: SegmentPropertyMap[] = [];
    let updatedGraph: SegmentationGraphSource | undefined;
    // ***
    for (const loadedSubsource of subsources) {
      const {subsourceEntry} = loadedSubsource;
      const {local} = subsourceEntry.subsource;
      const setProperties = (newProperties: AnnotationPropertySpec[]) => {
        if (properties !== undefined &&
            stableStringify(newProperties) !== stableStringify(properties)) {
          loadedSubsource.deactivate('Annotation properties are not compatible');
          return false;
        }
        properties = newProperties;
        return true;
      };
      if (local === LocalDataSource.annotations) {
        if (hasLocalAnnotations) {
          loadedSubsource.deactivate('Only one local annotations source per layer is supported');
          continue;
        }
        hasLocalAnnotations = true;
        if (!setProperties(this.localAnnotationProperties ?? [])) continue;
        loadedSubsource.activate(refCounted => {
          const localAnnotations = this.localAnnotations = new LocalAnnotationSource(
              loadedSubsource.loadedDataSource.transform, this.localAnnotationProperties ?? [],
            this.localAnnotationRelationships);
          try {
            localAnnotations.restoreState(this.localAnnotationsJson);
          } catch {
          }
          refCounted.registerDisposer(() => {
            localAnnotations.dispose();
            this.localAnnotations = undefined;
          });
          refCounted.registerDisposer(
              this.localAnnotations.changed.add(this.specificationChanged.dispatch));
          try {
            addPointAnnotations(this.localAnnotations, this.pointAnnotationsJson);
          } catch {
          }
          this.pointAnnotationsJson = undefined;
          this.localAnnotationsJson = undefined;
          const state = new AnnotationLayerState({
            localPosition: this.localPosition,
            transform: refCounted.registerDisposer(getWatchableRenderLayerTransform(
                this.manager.root.coordinateSpace, this.localPosition.coordinateSpace,
                loadedSubsource.loadedDataSource.transform, undefined)),
            source: localAnnotations.addRef(),
            displayState: this.annotationDisplayState,
            dataSource: loadedSubsource.loadedDataSource.layerDataSource,
            subsourceIndex: loadedSubsource.subsourceIndex,
            subsourceId: subsourceEntry.id,
            role: RenderLayerRole.ANNOTATION,
            segmentDisplayState: this.displayState
          });
          this.addAnnotationLayerState(state, loadedSubsource);
        });
        continue;
      }
      const {annotation} = subsourceEntry.subsource;
      if (annotation !== undefined) {
        if (!setProperties(annotation.properties)) continue;
        loadedSubsource.activate(() => {
          const state = new AnnotationLayerState({
            localPosition: this.localPosition,
            transform: loadedSubsource.getRenderLayerTransform(),
            source: annotation,
            displayState: this.annotationDisplayState,
            dataSource: loadedSubsource.loadedDataSource.layerDataSource,
            subsourceIndex: loadedSubsource.subsourceIndex,
            subsourceId: subsourceEntry.id,
            role: RenderLayerRole.ANNOTATION,
            segmentDisplayState: this.displayState
          });
          this.addAnnotationLayerState(state, loadedSubsource);
        });
        continue;
      }
      // ***
      const { segmentPropertyMap } = loadedSubsource.subsourceEntry.subsource;
      const isGroupRoot = this.displayState.linkedSegmentationGroup.root.value === this;
      if (segmentPropertyMap !== undefined) {
        if (!isGroupRoot) {
          loadedSubsource.deactivate(`Not supported on non-root linked segmentation layers`);
        } else {
          loadedSubsource.activate(() => { });
          updatedSegmentPropertyMaps.push(segmentPropertyMap);
          continue;
        }
      }
      // ***
      loadedSubsource.deactivate('Not compatible with annotation layer');
    }
    const prevAnnotationProperties = this.annotationDisplayState.annotationProperties.value;
    if (stableStringify(prevAnnotationProperties) !== stableStringify(properties)) {
      this.annotationDisplayState.annotationProperties.value = properties;
    }
    // ***
    // 预先存储聚类信息的PreprocessedSegmentPropertyMap
    const get_PreprocessedSegmentPropertyMap = getPreprocessedSegmentPropertyMap(this.manager.chunkManager, updatedSegmentPropertyMaps);;
    this.displayState.originalSegmentationGroupState.segmentPropertyMap.value = get_PreprocessedSegmentPropertyMap;
    if(get_PreprocessedSegmentPropertyMap){
      if(this.genefile.includes('.h5ad') || this.genefile.includes('.cellbin'))viewer.h5adfile_info_res[this.genefile] = get_PreprocessedSegmentPropertyMap
    }
    this.displayState.originalSegmentationGroupState.segmentPropertyMap.value =
      getPreprocessedSegmentPropertyMap(this.manager.chunkManager, updatedSegmentPropertyMaps);
    if(this.managedLayer.name.includes("cell_cluster_layer") && updatedSegmentPropertyMaps[0]?.inlineProperties?.filterProp === 'cellType'){//标识cellbin是否通过细胞类型过滤
      this.displayState.segmentationGroupState.value.filterByCellType = true;
      const inlineProperties = this.displayState.originalSegmentationGroupState.segmentPropertyMap.value?.segmentPropertyMap?.inlineProperties;
      if (inlineProperties?.ids?.length && inlineProperties?.ids?.length > 0) {
        let labelMap = {};
        const ids = new Uint32Array(inlineProperties.ids)
        inlineProperties.properties[0].values.forEach((item, index)=>{
          labelMap[JSON.stringify(ids[index*2]) ] = item
        })
        this.displayState.segmentationGroupState.value.propIdLabelMap = labelMap;
      }
    }
    // this.displayState.originalSegmentationGroupState.graph.value = updatedGraph;
    if(updatedGraph)this.displayState.originalSegmentationGroupState.graph.value = updatedGraph;
    // ***
  }

  initializeAnnotationLayerViewTab(tab: AnnotationLayerView) {
    const hasChunkedSource = tab.registerDisposer(makeCachedLazyDerivedWatchableValue(
        states => states.some(x => x.source instanceof MultiscaleAnnotationSource),
        this.annotationStates));
    const renderScaleControls = tab.registerDisposer(
        new DependentViewWidget(hasChunkedSource, (hasChunkedSource, parent, refCounted) => {
          if (!hasChunkedSource) return;
          {
            const renderScaleWidget = refCounted.registerDisposer(new RenderScaleWidget(
                this.annotationCrossSectionRenderScaleHistogram,
                this.annotationCrossSectionRenderScaleTarget));
            renderScaleWidget.label.textContent = 'Spacing (cross section)';
            parent.appendChild(renderScaleWidget.element);
          }
          {
            const renderScaleWidget = refCounted.registerDisposer(new RenderScaleWidget(
                this.annotationProjectionRenderScaleHistogram,
                this.annotationProjectionRenderScaleTarget));
            renderScaleWidget.label.textContent = 'Spacing (projection)';
            parent.appendChild(renderScaleWidget.element);
          }
        }));
    tab.element.insertBefore(renderScaleControls.element, tab.element.firstChild);
    {
      const checkbox = tab.registerDisposer(
          new TrackableBooleanCheckbox(this.annotationDisplayState.ignoreNullSegmentFilter));
      const label = document.createElement('label');
      label.appendChild(document.createTextNode('Ignore null related segment filter'));
      label.title =
          'Display all annotations if filtering by related segments is enabled but no segments are selected';
      label.appendChild(checkbox.element);
      tab.element.appendChild(label);
    }
    tab.element.appendChild(
        tab.registerDisposer(new LinkedSegmentationLayersWidget(this.linkedSegmentationLayers))
            .element);
  }

  toJSON() {
    const x = super.toJSON();
    x[CROSS_SECTION_RENDER_SCALE_JSON_KEY] = this.annotationCrossSectionRenderScaleTarget.toJSON();
    x[PROJECTION_RENDER_SCALE_JSON_KEY] = this.annotationProjectionRenderScaleTarget.toJSON();
    if (this.localAnnotations !== undefined) {
      x[ANNOTATIONS_JSON_KEY] = this.localAnnotations.toJSON();
    } else if (this.localAnnotationsJson !== undefined) {
      x[ANNOTATIONS_JSON_KEY] = this.localAnnotationsJson;
    }
    x[ANNOTATION_PROPERTIES_JSON_KEY] =
        annotationPropertySpecsToJson(this.localAnnotationProperties);
    const {localAnnotationRelationships} = this;
    x[ANNOTATION_RELATIONSHIPS_JSON_KEY] = (localAnnotationRelationships.length === 1 &&
                                            localAnnotationRelationships[0] === 'segments') ?
        undefined :
        localAnnotationRelationships;
    x[IGNORE_NULL_SEGMENT_FILTER_JSON_KEY] =
        this.annotationDisplayState.ignoreNullSegmentFilter.toJSON();
    x[SHADER_JSON_KEY] = this.annotationDisplayState.shader.toJSON();
    x[SHADER_CONTROLS_JSON_KEY] = this.annotationDisplayState.shaderControls.toJSON();
    Object.assign(x, this.linkedSegmentationLayers.toJSON());

    // 自定义颜色
    const { value: segmentStatedColors } = this.displayState.segmentStatedColors;
    if (segmentStatedColors.size > 0) {
      const j: any = x[SEGMENT_STATED_COLORS_JSON_KEY] = {};
      for (const [key, value] of segmentStatedColors) {
        j[key.toString()] = serializeColor(unpackRGB(value.low));
      }
    }
    // cluset选中项
    const { value: visibleSegments } = this.displayState.segmentationGroupState.value.visibleSegments;
    if (visibleSegments.size > 0) {
      x[SEGMENTS_JSON_KEY] = visibleSegments.toJSON();
      if(viewer.binSize.value === 'cellbin') x.segmentColors = viewer.self_newlabel_color[this.displayState.layer.genefile]
    }else if(viewer.binSize.value === h5ad_file_binsize && viewer.bin200_self_newlabel_color[this.displayState.layer.genefile]){
      x.segmentColors = viewer.bin200_self_newlabel_color[this.displayState.layer.genefile][viewer.binSize.value as string]
    }
    return x;
  }
  // ***cellborder新增
  moveToSegment(id: Uint64) {
    for (const layer of this.renderLayers) {
      if (!(layer instanceof MultiscaleMeshLayer)) continue;
      const layerPosition = layer.getObjectPosition(id);
      if (layerPosition === undefined) continue;
      this.setLayerPosition(
        layer.displayState.transform.value as RenderLayerTransform, layerPosition);
      return;
    }
    // StatusMessage.showTemporaryMessage(`No position information loaded for segment ${id}`);
  }
  // ***
  static type = 'annotation';
  static typeAbbreviation = 'ann';
}

export function makeShaderCodeWidget(layer: AnnotationUserLayer) {
  return new ShaderCodeWidget({
    shaderError: layer.annotationDisplayState.shaderError,
    fragmentMain: layer.annotationDisplayState.shader,
    shaderControlState: layer.annotationDisplayState.shaderControls,
  });
}

class ShaderCodeOverlay extends Overlay {
  codeWidget = this.registerDisposer(makeShaderCodeWidget(this.layer));
  constructor(public layer: AnnotationUserLayer) {
    super();
    this.content.appendChild(this.codeWidget.element);
    this.codeWidget.textEditor.refresh();
  }
}

// 颜色取反
class colorReversed extends Tab {
  colorBarReversed = this.registerDisposer(new colorBarView(this.layer))
  colorReversedElement = document.createElement('div')
  divElement = document.createElement('div')
  flag = false
  constructor(public layer: AnnotationUserLayer){
    super();
    let span = document.createElement('span');
    let ReversedText = document.createElement('p')
    let inputElement = document.createElement('input')
    ReversedText.innerText = 'Reverse'
    this.colorReversedElement.classList.add('container')
    this.divElement.classList.add('el-switch')
    inputElement.classList.add('el-switch__input');
    span.classList.add('el-switch__core');
    this.colorReversedElement.appendChild(this.divElement)
    this.divElement.appendChild(ReversedText)
    this.divElement.appendChild(inputElement)
    this.divElement.appendChild(span)
    this.divElement.addEventListener('click', ()=>{
      this.flag = !this.flag
      if(this.flag){
        this.divElement.classList.add('is-checked')
      }else{
        span.classList.add('unchecked')
        this.divElement.classList.remove('is-checked')
      }
      // this.reversed(this.flag)
    })
  }
  // reversed(flag:boolean){
  //   colorArr.forEach((item, index) =>{
  //     item.reversed = flag
  //     if(item.clicked){
  //       this.colorBarReversed.changeEdit( item, index,)
  //     }
  //   })
  // }
}

// colorBar  View
class colorBarView extends Tab {
  codeWidget = this.registerDisposer(makeShaderCodeWidget(this.layer));
  colorDiv = document.createElement('div');
  is_change_colorbar = new TrackableBoolean(false, false);
  constructor(public layer: AnnotationUserLayer){
    super();
    this.colorDiv.id = 'colorBarHq';
    this.makeUI(this.colorDiv)
  }
  changeEdit(arr: any, color: string){
    let clickColorArr:any
    clickColorArr = formatterRGB1(arr.colormap, arr.reversed);
    _layoutviewer.allDataSource.colorscale.colorData = clickColorArr;
    let dir = arr.reversed ?'bottom, ' : 'top, ';
    (document.getElementsByClassName('colorBarScale')[0] as HTMLElement).style.backgroundImage = 'linear-gradient(to '+ dir + [...arr.colormap] + ')';
    viewer.layerManager.managedLayers.forEach((item:any)=>{
      // 所有gef统一操作
      if(item.heatmap){
        (item.layer).genecolor = color;
        item.genecolor = color;
      }
    });
    _layoutviewer.allDataSource.colorscale.color = [...arr.colormap];
    _layoutviewer.allDataSource.colorscale.dir = dir;
    _layoutviewer.allDataSource.colorscale.genecolor = arr.colorName;
    _layoutviewer.display.scheduleRedraw();
  }
  makeUI(ele: HTMLElement){
    const seleDiv = makeElement('div', ['colorbar_select_area'], {});
    const seleTitle = makeElement('div', ['item_title','neuroglancer-layer-control-label-text-container'], {}, i18n.color);
    const subContent = this.getColorBarList();
    const button = new SelectView(null, {
      classes: ['lasso-binsize-view','mutil-select-colorbar'],
      defaultValue: subContent[1] as string,
      backgroundScheme: 'dark',
      childElement: subContent[0] as HTMLElement
    });
    seleDiv.appendChild(seleTitle);
    seleDiv.appendChild(button.element);
    ele.appendChild(seleDiv);
    this.is_change_colorbar.changed.add(()=>{
      if(this.is_change_colorbar.value){
        button.close();
        this.is_change_colorbar.value = false;
      }
    })
  }
  getColorBarList(){
    const binsizeContiner = makeElement('div',['binsize-container'],{});
    let curr_div: string = '';
    const ul = makeElement('ul',['el-select-dropdown__list']);
    for(let i = 0; i < colorArr.length; i++){
      let li = makeElement('li',['el-select-dropdown_item'],{});
      let span = makeElement('span',['colorbar_dropdown_item_span'])
      li.appendChild(span)
      if(this.layer.genecolor === colorArr[i].colorName){
        curr_div = `<span class="curr_color_sapn" style="
        height: 0.625rem;
        width: 90%;
        display: block;background-image: linear-gradient(to right, ${[...colorArr[i].colormap]})"></span>`
      }
      span.style.backgroundImage = 'linear-gradient(to right, ' + [...colorArr[i].colormap] + ')';
      if(this.layer.genecolor === colorArr[i].colorName){
        (document.getElementsByClassName('colorBarScale')[0] as HTMLElement).style.backgroundImage = 'linear-gradient(to top,' + [...colorArr[i].colormap] + ')';
      }
      li.addEventListener('click', ()=>{
        document.querySelectorAll('.ng-common-radio-colorbar').forEach((val)=>{
          if(val.classList.contains(`${colorArr[i].colorName}`)){
            (val as HTMLInputElement).checked = true;
          }else{
            (val as HTMLInputElement).checked = false;
          };
          this.layer.genecolor = colorArr[i].colorName;
          viewer.selectedLayer.layer!.genecolor = colorArr[i].colorName;
        })
        this.changeEdit(colorArr[i], colorArr[i].colorName);
        this.is_change_colorbar.value = true;
        let nodeList = document.body.querySelectorAll(".curr_color_sapn") as NodeList;
        nodeList.forEach((item:HTMLElement)=>item.style.backgroundImage = `linear-gradient(to right, ${[...colorArr[i].colormap]})`);
      })
      ul.appendChild(li);
    }
    binsizeContiner.appendChild(ul);
    return [binsizeContiner, curr_div]
  }
}

class colorBarRange extends Tab {
  element = document.createElement('div');
  codeWidget = this.registerDisposer(makeShaderCodeWidget(this.layer));
  constructor(public layer: AnnotationUserLayer) {
    super();
    const {element} = this;
    removeFromParent(element);
    element.classList.add('colorbar-range-div')
    const defatleDiv = document.createElement('div');
    defatleDiv.style.marginBottom = '1rem';
    defatleDiv.style.display = 'flex';
    defatleDiv.style.alignItems = 'center'
    const defaultInp = makeElement('input',['ng-common-radio'],{type: 'radio'});
    const defatleText = makeElement('span',['color-range-text'],{}, i18n.layerPanleDefault);
    const CustomeDiv = makeElement('div',['custome-flex-div','custome-all-div'],{});
    const CustomeInp = makeElement('input',['ng-common-radio'],{type: 'radio'});
    const CustomeText = makeElement('span',['color-range-text'],{},i18n.layerPanelCustomized);
    const CustomeInpText = makeElement('div',['color-range-custome-inp-text'],{});
    CustomeInpText.appendChild(CustomeInp);
    CustomeInpText.appendChild(CustomeText);
    const Range = makeElement('div',['custome-flex-div','colorbar-custome-range']);
    const rangemin = makeElement('input',['custome-input-range','custome-input-min'],{type: 'number', min: 0}) as HTMLInputElement;
    const rangeText = makeElement('span',[],{},`~`);
    const rangemax = makeElement('input',['custome-input-range','custome-input-max'],{type: 'number', min: 0}) as HTMLInputElement;
    if(this.layer.heatmap){
      (rangemin as HTMLInputElement).valueAsNumber = _layoutviewer.color_scale[allToolsButton.genelistFile].minValue;
      (rangemax as HTMLInputElement).valueAsNumber = _layoutviewer.color_scale[allToolsButton.genelistFile].maxValue;
      (defaultInp as HTMLInputElement).checked = _layoutviewer.color_scale[allToolsButton.genelistFile].isAutoScaleFlag;
      (CustomeInp as HTMLInputElement).checked = !_layoutviewer.color_scale[allToolsButton.genelistFile].isAutoScaleFlag;
      (rangemin as HTMLInputElement).disabled = viewer.color_scale[allToolsButton.genelistFile].isAutoScaleFlag;
      (rangemax as HTMLInputElement).disabled = viewer.color_scale[allToolsButton.genelistFile].isAutoScaleFlag;
      defatleDiv.appendChild(defaultInp);
      defatleDiv.appendChild(defatleText);
      CustomeDiv.appendChild(CustomeInpText);
      CustomeDiv.appendChild(Range);
      Range.appendChild(rangemin);
      Range.appendChild(rangeText);
      Range.appendChild(rangemax);
      element.appendChild(defatleDiv);
      element.appendChild(CustomeDiv);
    }else{
      element.style.display = 'none';
    }
    this.registerEventListener(rangemin, 'blur', ()=> {
      if(rangemin.valueAsNumber<0 || isNaN(rangemin.valueAsNumber)){
        (rangemin as HTMLInputElement).valueAsNumber = 0;
        return
      }
      this.range('custome', rangemin.valueAsNumber, rangemax.valueAsNumber)
    });
    this.registerEventListener(rangemax, 'blur', ()=> {
      if(rangemax.valueAsNumber<0 || isNaN(rangemax.valueAsNumber)){
        (rangemax as HTMLInputElement).valueAsNumber = viewer.color_scale[allToolsButton.genelistFile].defaultValue;;
        return
      }
      this.range('custome', rangemin.valueAsNumber, rangemax.valueAsNumber)
    });
    rangemin.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        rangemin.blur();
      }
      if(e.code === 'ArrowUp' || e.code === 'ArrowDown'){
        e.preventDefault();
      }
    });
    rangemax.addEventListener('keydown', (e) => {
      if (e.code.toLocaleLowerCase() === 'enter') {
        rangemax.blur();
      }
      if(e.code === 'ArrowUp' || e.code === 'ArrowDown'){
        e.preventDefault();
      }
    });
    this.registerEventListener(rangemax, 'change', ()=> {
      rangemax.blur();
    })
    this.registerEventListener(rangemin, 'change', ()=> {
      rangemin.blur();
    })
    this.registerEventListener(defaultInp, 'change', (e: Event)=> {
      if( (e.target as HTMLInputElement) ){
        (rangemin as HTMLInputElement).valueAsNumber = 0;
        (rangemax as HTMLInputElement).valueAsNumber = viewer.color_scale[allToolsButton.genelistFile].defaultValue;
        (CustomeInp as HTMLInputElement).checked = false;
        // (rangemin as HTMLInputElement).disabled = true;
        // (rangemax as HTMLInputElement).disabled = true;
        viewer.color_scale[allToolsButton.genelistFile].isAutoScaleFlag = true;
        this.range('auto', rangemin.valueAsNumber, rangemax.valueAsNumber);
      }
    });
    this.registerEventListener(CustomeInp, 'change', (e: Event)=> {
      if( (e.target as HTMLInputElement) ){
        (defaultInp as HTMLInputElement).checked = false;
        (rangemin as HTMLInputElement).disabled = false;
        (rangemax as HTMLInputElement).disabled = false;
        viewer.color_scale[allToolsButton.genelistFile].isAutoScaleFlag = false;
        this.range('custome', rangemin.valueAsNumber, rangemax.valueAsNumber);
      }
    });
  }
  range(type: string, min: number, max: number){
    if(Number(min) < 0 || Number(max) < 0 || isNaN(min) || isNaN(max) || min.toString().includes('.') || max.toString().includes('.')){
      new message(i18n.pleaseInteger, 'error');
      return
    }else{
      if(Number(max) > 50000000){
        new message(i18n.maximumOnline, 'error');
        return
      }else if(Number(min) > Number(max) || Number(min) === Number(max)){
        new message(i18n.maximumError, 'error');
        return
      }
    }
    if(type === 'auto'){
      (document.getElementsByClassName('custome-input-min')[0] as HTMLInputElement).valueAsNumber = 0;
      (document.getElementsByClassName('custome-input-max')[0] as HTMLInputElement).valueAsNumber = viewer.color_scale[allToolsButton.genelistFile].defaultValue;
      // 自动
      document.getElementsByClassName('custome-input-min')[0].setAttribute('disabled', 'disabled');
      document.getElementsByClassName('custome-input-max')[0].setAttribute('disabled', 'disabled');
      viewer.color_scale[allToolsButton.genelistFile].maxValue = viewer.color_scale[allToolsButton.genelistFile].defaultValue;
      viewer.color_scale[allToolsButton.genelistFile].minValue = 0;
    }else{
      // 自定义
      viewer.color_scale[allToolsButton.genelistFile].maxValue = Number(max);
      viewer.color_scale[allToolsButton.genelistFile].minValue = Number(min);
    }
    _layoutviewer.display.scheduleRedraw();
    viewer.layout.container.initColorScale();
  }
}

class tissueOutline extends Tab {
  element = document.createElement('div');
  button:SelectView | undefined = undefined;
  constructor(public layer: AnnotationUserLayer){
    super();
    let {element, button} = this;
    removeFromParent(element);
    element.classList.add('heatmap-tissueOutline-div');
    element.style.marginTop = '1rem';
    const seleDiv = makeElement('div', ['select_area'], { 'style': 'display: flex;justify-content: space-between;' });
    const seleTitle = makeElement('div',[],{},i18n.layerPanelTissueOutline);
    const subContent = this.getoutlineList((viewer.tissueGefOutline as tissueGefOutline)?.is_fill);
    button = new SelectView(null, {
      classes: ['lasso-binsize-view','mutil-select-tissueGeo'],
      defaultValue: viewer.tissueGefOutlineGeoData[allToolsButton.genelistFile] ? i18n['layerPanel'+(viewer.tissueGefOutline as tissueGefOutline)?.is_fill] : i18n.layerPanelNone,
      backgroundScheme: 'dark',
      childElement: subContent
    });
    button.element.style.width = '60%'
    if(!viewer.tissueGefOutlineGeoData[allToolsButton.genelistFile])button.element.classList.add('lasso_disabled');
    button.element.addEventListener('click',()=>{
      if((button as SelectView).element.classList.contains('lasso_disabled')) return
      (button as SelectView).element.getAttribute('data-checked') === 'true' ? (button as SelectView).element.appendChild(subContent) : (button as SelectView).element.removeChild(subContent);
    });
    seleDiv.appendChild(seleTitle);
    seleDiv.appendChild(button.element);
    element.appendChild(seleDiv);
    this.button = button;
  }
  getoutlineList(init: string){
    const list = viewer.tissueGefOutlineGeoData[allToolsButton.genelistFile] ? ['Outlined', 'Filled', 'Both', 'None'] : ['None']
    const binsizeContiner = makeElement('div',['binsize-container'],{});
    const ul = makeElement('ul',['el-select-dropdown__list']);
    if(viewer.tissueGefOutlineGeoData[allToolsButton.genelistFile]){
      list.forEach((item: string)=>{
        const li = makeElement('li',['el-select-dropdown_item'],{});
        const span = makeElement('span',[`binlist-${item}-cb`],{},`${i18n['layerPanel'+item]}`)
        li.appendChild(span);
        ul.appendChild(li);
        li.dataset.checked = item === init ? 'true' : 'false';
        li.addEventListener('click',()=>{
          (viewer.tissueGefOutline as tissueGefOutline).is_fill = item;
          (viewer.tissueGefOutline as tissueGefOutline).setGeoData(viewer.tissueGefOutlineGeoData[allToolsButton.genelistFile] );
          document.getElementsByClassName('mutil-select-tissueGeo')[0].getElementsByClassName('selecte-text')[0].innerHTML = i18n['layerPanel'+item];
          (this.button as SelectView).close();
        });
      })
    }
    binsizeContiner.appendChild(ul);
    return binsizeContiner
  }
}

class RenderingOptionsTab extends Tab {
  codeWidget = this.registerDisposer(makeShaderCodeWidget(this.layer));
  colorBar = this.registerDisposer(new colorBarView(this.layer));
  tissue_outline = this.registerDisposer( new tissueOutline(this.layer) );
  colorRange = this.registerDisposer(new colorBarRange(this.layer));
  urlInput: SourceUrlAutocomplete;
  // private selectBinView: SelectBinView|undefined;
  dataSourceView: DataSourceView;
  colorReverse = this.registerDisposer(new colorReversed(this.layer))
  constructor(public layer: AnnotationUserLayer) {
    super();
    const {element} = this;
    element.classList.add('neuroglancer-annotation-rendering-tab');
    element.appendChild(
        this
            .registerDisposer(new DependentViewWidget(
                layer.annotationDisplayState.annotationProperties,
                (properties, parent) => {
                  if (properties === undefined || properties.length === 0) return;
                  const propertyList = document.createElement('div');
                  parent.appendChild(propertyList);
                  propertyList.classList.add('neuroglancer-annotation-shader-property-list');
                  propertyList.style.display = 'none';
                  for (const property of properties) {
                    const div = document.createElement('div');
                    div.classList.add('neuroglancer-annotation-shader-property');
                    const typeElement = document.createElement('span');
                    typeElement.classList.add('neuroglancer-annotation-shader-property-type');
                    typeElement.textContent = property.type;
                    const nameElement = document.createElement('span');
                    nameElement.classList.add('neuroglancer-annotation-shader-property-identifier');
                    nameElement.textContent = `prop_${property.identifier}`;
                    div.appendChild(typeElement);
                    div.appendChild(nameElement);
                    const {description} = property;
                    if (description !== undefined) {
                      div.title = description;
                    }
                    propertyList.appendChild(div);
                  }
                }))
            .element);
    let topRow = document.createElement('div');
    topRow.className = 'neuroglancer-segmentation-dropdown-skeleton-shader-header';
    topRow.style.display = 'none';
    let label = document.createElement('div');
    label.style.flex = '1';
    label.textContent = 'Annotation shader:';
    topRow.appendChild(label);
    topRow.appendChild(makeMaximizeButton({
      title: 'Show larger editor view',
      onClick: () => {
        new ShaderCodeOverlay(this.layer);
      }
    }));
    topRow.appendChild(makeHelpButton({
      title: 'Documentation on annotation rendering',
      href:
          'https://github.com/google/neuroglancer/blob/master/src/neuroglancer/annotation/rendering.md',
    }));
    element.appendChild(topRow);

    element.appendChild(this.codeWidget.element);
    let cururl = this.layer.dataSources[0].spec_.url;    //  获取当前source面板的url
    // console.log(cururl)
    if(this.layer.heatmap){
      // this.selectBinView = this.selectBinView = new SelectBinView(this.layer.dataSources[0].loadState as LoadedLayerDataSource, cururl);
      // this.element.appendChild(this.selectBinView.selectBinContainer)
      // this.selectBinView.selectElement.onchange =() =>{
        // this.selectBinView?.onchange(cururl );
      // }
      element.appendChild(this.colorBar.colorDiv);
    }
    element.appendChild(this.registerDisposer(new ShaderControls(
                                                  layer.annotationDisplayState.shaderControls,
                                                  this.layer.manager.root.display, this.layer,
                                                  {visibility: this.visibility}))
                            .element);

    if(!this.layer.heatmap && cururl.includes('/annotation/cellbin')){
      let widget = new cellShapeModeWidget();
      element.appendChild(widget.element);
    }
    if(this.layer.heatmap){
      element.appendChild(this.tissue_outline.element);
      const scalebar = document.getElementsByClassName('neuroglancer-viewer-scalebar')[0] as HTMLElement;
      const colorBarTool = this.registerDisposer(new ToolControl(viewer.showColorbar, {
        classes: ['user-layer-heatmap-colorBar-control'],
        label: i18n.color_bar,
        onChange: () => {
          scalebar.style.display = viewer.showColorbar.value ? 'block' : 'none';
        }
      }));
      colorBarTool.element.style.marginBottom = '1rem';
      colorBarTool.element.style.marginTop = '1rem';
      colorBarTool.element.classList.add('tool-control-ColorBar')
      element.appendChild(colorBarTool.element)
      const {layersVisibleChanged} = viewer.layerManager
      layersVisibleChanged.add(() => {
        const hasHeatmap = viewer.layerManager.getLayerVisible('heatmap')
        colorBarTool.setDisable(!hasHeatmap || viewer.proteinMergeColor.value)
        // viewer.showColorbar.value = hasHeatmap
      });
      element.appendChild(this.colorRange.element);
    }
    if(Object.keys(viewer.saveSelectedKeyVal).length > 0 ){
      element.appendChild(getDisplayScheme(this.layer));
    }
    // element.appendChild(this.colorReverse.colorReversedElement);
  }
}

registerLayerType(AnnotationUserLayer);
registerLayerType(AnnotationUserLayer, 'pointAnnotation');
registerLayerTypeDetector(subsource => {
  if (subsource.local === LocalDataSource.annotations) {
    return {layerConstructor: AnnotationUserLayer, priority: 100};
  }
  if (subsource.annotation !== undefined) {
    return {layerConstructor: AnnotationUserLayer, priority: 1};
  }
  return undefined;
});

registerLayerShaderControlsTool(
    AnnotationUserLayer, layer => ({
                           shaderControlState: layer.annotationDisplayState.shaderControls,
                         }));


// function formatterRGB(arr:any, flag: boolean){
//   var result = [];
//   var result_ = '';
//   for(let i = 0; i < arr.length; i++){
//     var sub = [];
//     var item = arr[i].substring(1);
//     var r = (parseInt(item.substring(0,2),16) / 255).toFixed(6);
//     var g = (parseInt(item.substring(2,4),16) / 255).toFixed(6);
//     var b = (parseInt(item.substring(4,6),16) / 255).toFixed(6);
//     sub[0] = r;
//     sub[1] = g;
//     sub[2] = b;
//     result.push(sub);
//   };
//   result = flag ? result.reverse() : result;
//   result.forEach((item,index:number)=>{
//     if(index === result.length-1){
//       result_=result_+'vec3 ('+item.toString()+ ')';
//     }else{
//       result_=result_+'vec3 ('+item.toString()+ '),';
//     }
//   });
//   return `vec3[${arr.length}](${result_})`;
// }

export function formatterRGB1(arr:any, flag: boolean){
  var result = [];
  for(let i = 0; i < arr.length; i++){
    var item = arr[i].substring(1);
    var r = Number((parseInt(item.substring(0,2),16) / 255).toFixed(2));
    var g = Number((parseInt(item.substring(2,4),16) / 255).toFixed(2));
    var b = Number((parseInt(item.substring(4,6),16) / 255).toFixed(2));
    result.push(r, g, b);
  };
  result = flag ? result.reverse() : result;
  return result;
}

function getDisplayScheme(layer: AnnotationUserLayer){
  const moreContent = makeElement('div');
  const title = makeElement('div',[],{},i18n.display_scheme);
  const content = makeElement('div');
  const Mutil = makeElement('div',[],{style: 'padding: 0.5rem 0; display: flex;align-items: center;'});
  const mutilInput = makeElement('input', ['ng-common-radio', 'more-mutilColor'],{type: 'radio'})  as HTMLInputElement;
  mutilInput.checked = viewer.proteinMergeColor.value;
  const mutilText = makeElement('span',[],{style: 'margin-left: .5rem'},i18n.multi_color);
  const heatmap = makeElement('div',[],{style: ' display: flex;align-items: center;'});
  const heatInput = makeElement('input', ['ng-common-radio', 'more-heatMap'],{type: 'radio'})  as HTMLInputElement;
  heatInput.checked = !viewer.proteinMergeColor.value;
  const heatText = makeElement('span',[],{style: 'margin-left: .5rem'},i18n.heat_map);
  moreContent.appendChild(title);
  moreContent.appendChild(content);
  content.appendChild(Mutil);
  content.appendChild(heatmap);
  Mutil.appendChild(mutilInput);
  Mutil.appendChild(mutilText);
  heatmap.appendChild(heatInput);
  heatmap.appendChild(heatText);
  heatInput.addEventListener('click',()=>{
    if(document.getElementsByClassName('neuroglancer-icon-transform') && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked')==='true'){
      heatInput.checked = true;
      return
    }
    const table = document.getElementsByClassName('genetable-selected-table')[0];
    table?.classList?.add('mutil-color-property-show');
    if (document.getElementsByClassName('gene-save-filter')[0]) {
      (document.getElementsByClassName('gene-save-filter')[0] as HTMLElement).style.display = 'none';
    }
    heatInput.checked = true;
    mutilInput.checked = false;
    let json = viewer.state.toJSON();
    let layers = json.layers;
    layers.forEach((item:any)=>{
      if(item.heatmap && item.genename === allToolsButton.genelistFile){
        item.hasData = true;
        item.visible = true;
        item.disabled = false;
        item.source = item.source.replace(/bin.*/, viewer.binSize.value);
      }
      if(item.genetype === 'proteinimage' && !item.name.includes('IF')){
        item.visible = false;
      }
    })
    json.layers = layers;
    json.imageTools.showColorbar = true;
    json.imageTools.showLegend = false;
    // 由于layervisiblechange在restoreState后，所以需要在restoreState之前修改proteinMergeColor
    viewer.proteinMergeColor.value = false;
    viewer.configDialog?.close();
    viewer.state.restoreState(json);
    viewer.showColorbar.value = true;
    viewer.showLegend.value = false;
    const currentActivedTool = layer.managedLayer.name;
    const dom = document.getElementById(currentActivedTool);
    viewer.selectedLayer.layer = viewer.layerManager.getLayerByName(currentActivedTool);
    viewer.selectedLayer.visible = true;
    viewer.configStorage.clear();
    setTimeout(() => {
      dom?.setAttribute('data-selected', 'true')
      viewer.configStorage.registParentSingle(currentActivedTool, dom!) // 每次点击选中都注册，避免restoState的时候dom更新，配置窗位置异常
      viewer.configStorage.setActivedTool(currentActivedTool)
    }, 100);
  });
  mutilInput.addEventListener('click',()=>{
    if(document.getElementsByClassName('neuroglancer-icon-transform') && document.getElementsByClassName('neuroglancer-icon-transform')[0]?.getAttribute('data-checked')==='true'){
      mutilInput.checked = false;
      return
    }
    const table = document.getElementsByClassName('genetable-selected-table')[0];
    table?.classList.remove('mutil-color-property-show');
    if (document.getElementsByClassName('gene-save-filter')[0]) {
      (document.getElementsByClassName('gene-save-filter')[0] as HTMLElement).style.display = 'flex';
    }
    let json = viewer.state.toJSON();
    let layers = json.layers;
    heatInput.checked = false;
    mutilInput.checked = true;
    layers.forEach((item:any)=>{
      if(item.heatmap){
        item.visible = item.genename === allToolsButton.genelistFile;
        item.hasData = item.genename === allToolsButton.genelistFile;
        item.disabled = !(item.genename === allToolsButton.genelistFile);
      }
      if(item.genetype === 'proteinimage' && !item.name.includes('IF')){
        item.visible = viewer.saveSelectedKeyVal[item.genename][2] === 1;
        item.hasData = true;
        item.source = `precomputed://${local_host_SN_flag}/image/gene/${item.genename}/${allToolsButton.genelistFile}/${viewer.binSize.value}`
      }
    })
    json.layers = layers;
    json.imageTools.showColorbar = false;
    json.imageTools.showLegend = true;
    viewer.proteinMergeColor.value = true;
    viewer.configDialog?.close();
    viewer.state.restoreState(json, false);
    viewer.showColorbar.value = false;
    viewer.showLegend.value = true;
    const currentActivedTool = layer.managedLayer.name;
    const dom = document.getElementById(currentActivedTool);
    viewer.selectedLayer.layer = viewer.layerManager.getLayerByName(currentActivedTool);
    viewer.selectedLayer.visible = true;
    viewer.configStorage.clear();
    setTimeout(() => {
      dom?.setAttribute('data-selected', 'true')
      viewer.configStorage.registParentSingle(currentActivedTool, dom!) // 每次点击选中都注册，避免restoState的时候dom更新，配置窗位置异常
      viewer.configStorage.setActivedTool(currentActivedTool)
    }, 100);
  })
  return moreContent
}
